import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/api';
import { readAdminApiErrorMessage } from '../utils/adminUi';
import { mapWorkflowActionToApi } from '../utils/workflowActions';
import { showToast } from '../utils/toast';
import SearchableDropdown from './shared/SearchableDropdown';
import { trackEvent } from '../services/applicationInsights';
import {
  ORG_STORAGE_KEY,
  SITE_STORAGE_KEY,
  getInitialOrgId,
  persistOrgSelection,
  getInitialSiteId,
  persistSiteSelection,
  validateAndReconcileSelection
} from './utils/adminScopeStorage';
import {
  FaFilter,
  FaTrash,
  FaCheck,
  FaTimes,
  FaSearch,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaSyncAlt,
  FaFolderOpen,
  FaQuestionCircle,
  FaCubes,
  FaHourglassHalf,
  FaCheckCircle,
  FaBan,
  FaExclamationTriangle,
  FaEye
} from "react-icons/fa";

function normalizeVisibilityRecords(records) {
  if (!Array.isArray(records)) {
    return [];
  }

  return records
    .filter((record) => record && typeof record === 'object')
    .map((record) => ({
      ...record,
      id: getVisibilityRecordId(record)
    }));
}

function getVisibilityRecordId(record) {
  return record?.id || record?.visibilityId || record?.artifactId || record?.ruleId || '';
}

export default function Visibility({ token, roles = [], orgId }) {
  const isSystemAdmin = roles.includes('SystemAdministrator');
  const isOrgAdmin = roles.includes('OrganizationAdministrator');
  const tableRef = useRef(null);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(() => getInitialOrgId(isSystemAdmin, orgId));
  const [sitesForLoad, setSitesForLoad] = useState([]);
  const [selectedSite, setSelectedSite] = useState(() => getInitialSiteId());
  const [selectedFlow, setSelectedFlow] = useState('');
  const [flowsForLoad, setFlowsForLoad] = useState([]);
  const [visibilities, setVisibilities] = useState([]);
  const [selectedFlowStatus, setSelectedFlowStatus] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingOrgs, setFetchingOrgs] = useState(false);
  const [fetchingSites, setFetchingSites] = useState(false);
  const [fetchingFlows, setFetchingFlows] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [selectedVisibilityDetails, setSelectedVisibilityDetails] = useState(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);



  const siteIdsFromToken = (() => {
    try {
      if (!token) return [];
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return (payload?.site_ids || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    } catch (e) { return []; }
  })();

  const isOwnedSite = (siteId) => siteIdsFromToken.includes(String(siteId).toLowerCase());
  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);


  const parseVisibilityListError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'Invalid visibility query. Please verify flow and organization context.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view visibility rules for this flow.',
      404: 'No visibility rules found for the selected flow.',
      500: 'An error occurred while retrieving visibility rules.',
      default: 'Could not retrieve visibility rules.'
    });
  }, []);

  const parseVisibilityActionError = useCallback((res, type) => {
    const fallbackByType = {
      DELETE: 'Failed to delete visibility rule.',
      APPROVE: 'Failed to approve visibility rule.',
      REJECT: 'Failed to reject visibility rule.',
      DISABLE: 'Failed to disable visibility rule.'
    };

    return readAdminApiErrorMessage(res, {
      400: type === 'DELETE'
        ? 'Invalid visibility ID.'
        : type === 'DISABLE'
          ? 'Workflow data is invalid for disable action.'
          : 'Workflow data is invalid for visibility review.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to perform this visibility action.',
      404: type === 'DELETE'
        ? 'Visibility not found or delete failed.'
        : 'The requested visibility record was not found.',
      409: 'This visibility action conflicts with the current workflow state.',
      500: 'An unexpected server error occurred while processing the visibility action.',
      default: fallbackByType[type] || 'Visibility action failed.'
    });
  }, []);

  const parseVisibilityDetailError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      400: 'Invalid visibility ID.',
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view visibility details.',
      404: 'Visibility rule not found.',
      500: 'An error occurred while loading visibility details.',
      default: 'Could not load visibility details.'
    });
  }, []);

  const parseOrganizationError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view organizations.',
      500: 'An error occurred while loading organizations.',
      default: 'Failed to load organizations.'
    });
  }, []);

  const parseSiteCollectionsError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view site collections.',
      404: 'Site collections for the selected organization were not found.',
      500: 'An error occurred while loading site collections.',
      default: 'Failed to load site collections.'
    });
  }, []);

  const parseFlowListError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view flows for this site.',
      404: 'No flows found for the selected site.',
      500: 'An error occurred while loading user flows.',
      default: 'Failed to load user flows.'
    });
  }, []);

  const parseFlowStatusError = useCallback((res) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view the selected flow.',
      404: 'Flow not found for the selected organization.',
      500: 'An error occurred while loading flow status.',
      default: 'Failed to load flow status.'
    });
  }, []);

  // Fetch flow status when selectedFlow changes
  useEffect(() => {
    if (!selectedFlow) {
      setSelectedFlowStatus(null);
      return;
    }

    const fetchFlowStatus = async () => {
      try {
        const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
        const res = await fetch(`${API_BASE_URL}/userflows/${selectedFlow}?organizationId=${orgIdToUse}`, {
          headers: getHeaders()
        });
        if (!res.ok) {
          const message = await parseFlowStatusError(res);
          setSelectedFlowStatus(null);
          showToast.error(message);
          return;
        }

        const flowData = await res.json().catch(() => null);
        setSelectedFlowStatus(flowData?.status || null);
      } catch (err) {
        setSelectedFlowStatus(null);
        showToast.error(err?.message || 'Failed to load flow status.');
      }
    };

    fetchFlowStatus();
  }, [selectedFlow, selectedOrg, orgId, isSystemAdmin, getHeaders, parseFlowStatusError]);

  const loadVisibilities = useCallback(async () => {
    if (!selectedFlow) return;
    setLoading(true);
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    try {
      const res = await fetch(`${API_BASE_URL}/visibility/userflows/${selectedFlow}?organizationId=${orgIdToUse}`, { headers: getHeaders() });
      if (!res.ok) {
        const message = await parseVisibilityListError(res);
        if (res.status === 404 && /No visibility rules found/i.test(message)) {
          setVisibilities([]);
          setCurrentPage(1);
          return;
        }

        throw new Error(message);
      }

      const data = await res.json().catch(() => []);
      if (typeof data === 'string' && /No visibility rules found/i.test(data)) {
        setVisibilities([]);
      } else {
        setVisibilities(normalizeVisibilityRecords(data));
      }
      setCurrentPage(1);
    } catch (err) {
      showToast.error(err.message || 'Could not retrieve visibility rules.');
      setVisibilities([]);
    } finally { setLoading(false); }
  }, [selectedFlow, isSystemAdmin, selectedOrg, orgId, getHeaders, parseVisibilityListError]);

  useEffect(() => {
    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    if (orgIdToUse && selectedSite && selectedFlow) {
      loadVisibilities();
    } else {
      setVisibilities([]);
    }
  }, [selectedOrg, orgId, isSystemAdmin, selectedSite, selectedFlow, loadVisibilities]);

  const handleViewDetails = async (visibilityId) => {
    if (!visibilityId) return;
    setShowDetailsModal(true);
    setFetchingDetails(true);
    setSelectedVisibilityDetails(null);

    const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
    try {
      const res = await fetch(`${API_BASE_URL}/visibility/${encodeURIComponent(visibilityId)}?organizationId=${encodeURIComponent(orgIdToUse || '')}`, {
        headers: getHeaders()
      });

      if (!res.ok) {
        const message = await parseVisibilityDetailError(res);
        showToast.error(message);
        return;
      }

      const data = await res.json().catch(() => null);
      if (data && typeof data === 'object') {
        setSelectedVisibilityDetails(data);
      } else {
        throw new Error('Received invalid visibility response.');
      }
    } catch (err) {
      showToast.error(err?.message || 'Could not load visibility details.');
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleKpiClick = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const executeAction = async () => {
    if (!confirmation) return;
    const { id, type } = confirmation;
    setSubmitting(true);
    setConfirmation(null);

    try {
      const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;

      if (type === 'DELETE') {
        const res = await fetch(`${API_BASE_URL}/visibility/${id}?organizationId=${orgIdToUse}`, {
          method: 'DELETE',
          headers: getHeaders()
        });

        if (!res.ok) {
          const message = await parseVisibilityActionError(res, type);
          throw new Error(message);
        }

        showToast.success('Rule removed successfully');
      } else if (type === 'APPROVE' || type === 'REJECT') {
        if (type === 'APPROVE' && selectedFlowStatus !== 'Approved') {
          throw new Error(`The parent user flow must be approved before this visibility rule can be approved. Current flow status: ${selectedFlowStatus || 'Unknown'}`);
        }

        const requestBody = {
          action: mapWorkflowActionToApi(type),
          artifactId: id,
          artifactType: 'Visibility'
        };

        const res = await fetch(`${API_BASE_URL}/workflows/approve-or-reject?organizationId=${orgIdToUse}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(requestBody)
        });

        if (!res.ok) {
          const message = await parseVisibilityActionError(res, type);
          throw new Error(message);
        }

        const actionText = type === 'APPROVE' ? 'approved' : 'rejected';
        showToast.success(`Rule ${actionText} successfully`);
      } else if (type === 'DISABLE') {
        const requestBody = {
          artifactType: 'Visibility',
          action: mapWorkflowActionToApi(type),
          artifactId: id
        };

        const res = await fetch(`${API_BASE_URL}/workflows/disable?organizationId=${orgIdToUse}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(requestBody)
        });

        if (!res.ok) {
          const message = await parseVisibilityActionError(res, type);
          throw new Error(message);
        }

        showToast.success('Visibility rule disabled successfully');
        setStatusFilter('All');
        setCurrentPage(1);
      }

      await loadVisibilities();
    } catch (err) {
      showToast.error(err.message || 'Unable to complete visibility action.');
    } finally {
      setSubmitting(false);
    }
  };

  // Load Organizations
  useEffect(() => {
    if (isSystemAdmin) {
      setFetchingOrgs(true);
      fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders() })
        .then(async (res) => {
          if (!res.ok) {
            const message = await parseOrganizationError(res);
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setOrganizations(list);
          setSelectedOrg((prev) => validateAndReconcileSelection(list, prev, isSystemAdmin ? ORG_STORAGE_KEY : null));
        })
        .catch((err) => showToast.error(err?.message || 'Error loading organizations.'))
        .finally(() => setFetchingOrgs(false));
    }
  }, [isSystemAdmin, getHeaders, parseOrganizationError]);

  // Load Sites and reset Site/Flow when Org changes
  useEffect(() => {
    const orgToUse = isSystemAdmin ? selectedOrg : orgId;
    if (orgToUse) {
      setFetchingSites(true);
      fetch(`${API_BASE_URL}/site-collections?organizationId=${orgToUse}`, { headers: getHeaders() })
        .then(async (res) => {
          if (!res.ok) {
            const message = await parseSiteCollectionsError(res);
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          setSitesForLoad(list);
          setSelectedSite((prev) => validateAndReconcileSelection(list, prev, SITE_STORAGE_KEY));
        })
        .catch((err) => {
          showToast.error(err?.message || 'Error loading site collections.');
          setSitesForLoad([]);
          setSelectedSite('');
        })
        .finally(() => setFetchingSites(false));
    } else {
      setSitesForLoad([]);
      setSelectedSite('');
      setSelectedFlow('');
      setFlowsForLoad([]);
      setVisibilities([]);
      setSelectedFlowStatus(null);
    }
  }, [selectedOrg, orgId, isSystemAdmin, getHeaders, parseSiteCollectionsError]);

  // Load Flows and reset Flow/Visibilities when Site changes
  useEffect(() => {
    const orgToUse = isSystemAdmin ? selectedOrg : orgId;
    if (selectedSite && orgToUse) {
      setFetchingFlows(true);
      fetch(`${API_BASE_URL}/userflows/site-collections/${selectedSite}?organizationId=${orgToUse}`, { headers: getHeaders() })
        .then(async (res) => {
          if (!res.ok) {
            const message = await parseFlowListError(res);
            if (res.status === 404 && /No flows found/i.test(message)) {
              return [];
            }
            throw new Error(message);
          }

          return res.json().catch(() => []);
        })
        .then(data => setFlowsForLoad(Array.isArray(data) ? data : []))
        .catch((err) => {
          showToast.error(err?.message || 'Error loading user flows.');
          setFlowsForLoad([]);
        })
        .finally(() => setFetchingFlows(false));
    } else {
      setFlowsForLoad([]);
    }
    // Reset flow and visibilities when site changes
    setSelectedFlow('');
    setVisibilities([]);
    setSelectedFlowStatus(null);
  }, [selectedSite, selectedOrg, isSystemAdmin, getHeaders, orgId, parseFlowListError]);

  const filteredVisibilities = useMemo(() => {
    return visibilities.filter(v => {
      const matchesSearch = String(v?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(v?.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'All' || v.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [visibilities, searchTerm, statusFilter]);

  const confirmationActionLabel = confirmation?.type === 'APPROVE'
    ? 'Approve'
    : confirmation?.type === 'REJECT'
      ? 'Reject'
      : confirmation?.type === 'DISABLE'
        ? 'Disable'
        : 'Delete';

  const totalPages = Math.ceil(filteredVisibilities.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredVisibilities.slice(start, start + itemsPerPage);
  }, [filteredVisibilities, currentPage]);

  const visiblePages = useMemo(() => {
    const maxVisible = 5;
    if (totalPages <= maxVisible) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const getBadgeStyle = (status) => {
    const base = {
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      transition: 'all 0.2s ease'
    };

    if (status === 'Approved') {
      return { ...base, background: '#dcfce7', color: '#166534' };
    }
    if (status === 'Rejected') {
      return { ...base, background: '#fee2e2', color: '#991b1b' };
    }
    if (status === 'Submitted') {
      return { ...base, background: '#e9d5ff', color: '#581c87' };
    }
    if (status === 'Pending') {
      return { ...base, background: '#fef3c7', color: '#92400e' };
    }
    if (status === 'Disabled') {
      return {
        ...base,
        background: '#f3f4f6',
        color: '#6b7280',
        border: '1px solid #d1d5db',
        fontWeight: 600,
        opacity: 0.85
      };
    }
    return base;
  };

  const showFlowWarning = selectedFlow && selectedFlowStatus && selectedFlowStatus !== 'Approved';

  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaShieldAlt size={22} /></div>
            <div><h1 style={titleStyle}>Visibility Control</h1></div>
          </div>
        </header>

        {/* SELECTION CRITERIA CARD */}
        <div style={filtersCardStyle}>
          <div style={cardHeaderStyle}><FaFilter color="#6366f1" /><span style={cardTitleStyle}>Selection Criteria</span></div>
          <div style={filtersGridStyle}>
            {isSystemAdmin && (
              <div style={inputGroupStyle}>
                <label style={labelStyle}>Organization</label>
                <SearchableDropdown
                  options={organizations}
                  selectedValue={selectedOrg}
                  onSelect={(value) => {
                    setSelectedOrg(value);
                    persistOrgSelection(isSystemAdmin, value);
                    setSelectedSite('');
                    setSelectedFlow('');
                    if (value && selectedSite) {
                      trackEvent('Dropdown Selected', {
                        section: 'visibility',
                        organizationId: value,
                        siteCollectionId: selectedSite
                      });
                    }
                  }}
                  placeholder="Choose Organization"
                  displayKey="name"
                  valueKey="id"
                  isLoading={fetchingOrgs}
                  disabled={fetchingOrgs}
                  styles={searchableDropdownStyles}
                />
              </div>
            )}

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Site Collection</label>
              <SearchableDropdown
                options={sitesForLoad}
                selectedValue={selectedSite}
                onSelect={(value) => {
                  setSelectedSite(value);
                  persistSiteSelection(value);
                  setSelectedFlow('');
                  const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
                  if (value && orgIdToUse) {
                    trackEvent('Dropdown Selected', {
                      section: 'visibility',
                      organizationId: orgIdToUse,
                      siteCollectionId: value
                    });
                  }
                }}
                placeholder={!selectedOrg && isSystemAdmin ? 'Select Org First' : 'Choose Site'}
                displayKey="name"
                valueKey="id"
                isLoading={fetchingSites}
                disabled={fetchingSites || (isSystemAdmin && !selectedOrg)}
                emptyMessage={sitesForLoad.length === 0 ? 'No sites available' : 'No results found'}
                styles={searchableDropdownStyles}
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>User Flow</label>
              <SearchableDropdown
                options={flowsForLoad}
                selectedValue={selectedFlow}
                onSelect={(value) => {
                  setSelectedFlow(value);
                  const orgIdToUse = isSystemAdmin ? selectedOrg : orgId;
                  if (value && selectedSite && orgIdToUse) {
                    trackEvent('Dropdown Selected', {
                      section: 'visibility',
                      organizationId: orgIdToUse,
                      siteCollectionId: selectedSite,
                      flowId: value
                    });
                  }
                }}
                placeholder={!selectedSite ? 'Select Site First' : 'Choose Flow'}
                displayKey="flowName"
                valueKey="id"
                isLoading={fetchingFlows}
                disabled={fetchingFlows || !selectedSite}
                emptyMessage={flowsForLoad.length === 0 ? 'No flows available' : 'No results found'}
                styles={searchableDropdownStyles}
              />
            </div>

            <div style={{ ...inputGroupStyle, justifyContent: 'flex-end' }}>
              <button
                onClick={loadVisibilities}
                disabled={!selectedFlow || loading || submitting}
                style={{
                  ...primaryButtonStyle,
                  opacity: !selectedFlow || loading || submitting ? 0.6 : 1,
                  cursor: !selectedFlow || loading || submitting ? 'not-allowed' : 'pointer'
                }}
              >
                <FaSyncAlt style={{ animation: (loading || submitting) ? 'spin 1s linear infinite' : 'none' }} />
                {(loading || submitting) ? 'Fetching...' : 'Sync Visibility Rules'}
              </button>
            </div>
          </div>
        </div>

        {/* FLOW STATUS WARNING */}
        {showFlowWarning && (
          <div style={flowWarningBannerStyle}>
            <FaExclamationTriangle size={20} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Parent Flow Not Approved</div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                The selected User Flow has status: <span style={flowStatusBadge}>{selectedFlowStatus}</span>.
                You must <b>APPROVE the User Flow first</b> before you can approve any visibility rules associated with it.
              </div>
            </div>
          </div>
        )}

        {/* KPI ROW */}
        <div style={kpiRowStyle}>
          <div className="kpi-card" style={statusFilter === 'All' ? kpiCardActiveAll : kpiCardWhite} onClick={() => handleKpiClick('All')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f5f3ff', color: '#6366f1' }}><FaCubes /></div>
              <span style={kpiLabelStyle}>Total Rules</span>
            </div>
            <div style={kpiValueStyle}>{visibilities.length}</div>
            <div style={kpiMetaStyle}>All visibility rules</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Pending' ? kpiCardActivePending : kpiCardWhite} onClick={() => handleKpiClick('Pending')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#fffbeb', color: '#d97706' }}><FaHourglassHalf /></div>
              <span style={kpiLabelStyle}>Pending</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#d97706' }}>{visibilities.filter(v => v?.status === 'Pending').length}</div>
            <div style={kpiMetaStyle}>Draft rules</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Submitted' ? kpiCardActiveSubmitted : kpiCardWhite} onClick={() => handleKpiClick('Submitted')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f3e8ff', color: '#7c3aed' }}><FaHourglassHalf /></div>
              <span style={kpiLabelStyle}>Submitted</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#7c3aed' }}>{visibilities.filter(v => v?.status === 'Submitted').length}</div>
            <div style={kpiMetaStyle}>Awaiting approval</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Approved' ? kpiCardActiveApproved : kpiCardWhite} onClick={() => handleKpiClick('Approved')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f0fdf4', color: '#16a34a' }}><FaCheckCircle /></div>
              <span style={kpiLabelStyle}>Approved</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#10b981' }}>{visibilities.filter(v => v?.status === 'Approved').length}</div>
            <div style={kpiMetaStyle}>Active rules</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Rejected' ? kpiCardActiveRejected : kpiCardWhite} onClick={() => handleKpiClick('Rejected')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#fef2f2', color: '#dc2626' }}><FaTimes /></div>
              <span style={kpiLabelStyle}>Rejected</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#ef4444' }}>{visibilities.filter(v => v?.status === 'Rejected').length}</div>
            <div style={kpiMetaStyle}>Declined rules</div>
          </div>

          <div className="kpi-card" style={statusFilter === 'Disabled' ? kpiCardActiveDisabled : kpiCardWhite} onClick={() => handleKpiClick('Disabled')}>
            <div style={kpiHeader}>
              <div style={{ ...kpiIconWrapper, background: '#f3f4f6', color: '#6b7280' }}><FaBan /></div>
              <span style={kpiLabelStyle}>Disabled</span>
            </div>
            <div style={{ ...kpiValueStyle, color: '#6b7280' }}>{visibilities.filter(v => v?.status === 'Disabled').length}</div>
            <div style={kpiMetaStyle}>Inactive rules</div>
          </div>
        </div>

        {/* TABLE CARD */}
        <div ref={tableRef} style={tableCardStyle}>
          <div style={tableActionsHeader}>
            <div style={searchWrapper}>
              <FaSearch style={searchIcon} />
              <input style={searchInput} placeholder="Search visibility rules..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Rule Configuration</th>
                <th style={thStyle}>Status</th>
                <th style={thActionStyle}>Control</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3" style={emptyStateStyle}><FaSyncAlt className="spin" /> Synchronizing...</td></tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="3" style={emptyStateStyle}>
                    <FaFolderOpen size={32} opacity={0.3} />
                    <div style={{ marginTop: 10 }}>{!selectedFlow ? 'Select a flow to begin' : 'No visibility rules found'}</div>
                  </td>
                </tr>
              ) : (
                currentItems.map((v, index) => {
                  const visibilityId = getVisibilityRecordId(v);
                  const isConfirming = Boolean(confirmation && visibilityId && confirmation.id === visibilityId);

                  return (
                    <tr key={visibilityId || `visibility-row-${index}`} style={trStyle}>
                      <td style={tdStyle}>
                        <div style={ruleTitle}>{v.name}</div>
                        <div style={ruleIdText}>ID: {visibilityId || 'Unavailable'}</div>
                        <div style={ruleDesc}>{v.description || 'No description provided'}</div>
                      </td>
                      <td style={tdStyle}>
                        <span style={getBadgeStyle(v.status)}><FaCircle size={6} /> {v.status}</span>
                      </td>
                      <td style={tdActionStyle}>
                        {isConfirming ? (
                          <div style={inlineConfirmWrapper}>
                            <FaQuestionCircle color="#6366f1" />
                            <span style={confirmText}>Confirm {confirmationActionLabel}?</span>
                            <button style={btnYes} onClick={executeAction} disabled={loading || submitting}>Yes</button>
                            <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                          </div>
                        ) : (
                          <div style={actionButtonGroupStyle}>
                            {visibilityId && (
                              <button
                                title="View Details"
                                style={actionBtnView}
                                onClick={() => handleViewDetails(visibilityId)}
                                aria-label="View Details"
                              >
                                <FaEye />
                              </button>
                            )}
                            {v.status === "Submitted" && visibilityId && (isSystemAdmin || isOrgAdmin || isOwnedSite(v.siteId)) && (
                              <>
                                <button
                                  title={selectedFlowStatus !== 'Approved' ? 'Parent flow must be approved first' : 'Approve'}
                                  style={{
                                    ...actionBtnApprove,
                                    opacity: selectedFlowStatus !== 'Approved' ? 0.5 : 1,
                                    cursor: selectedFlowStatus !== 'Approved' ? 'not-allowed' : 'pointer'
                                  }}
                                  disabled={selectedFlowStatus !== 'Approved'}
                                  onClick={() => {
                                    if (selectedFlowStatus !== 'Approved') {
                                      showToast.error(`The parent user flow must be approved before this visibility rule can be approved. Current flow status: ${selectedFlowStatus || 'Unknown'}`);
                                      return;
                                    }

                                    setConfirmation({ id: visibilityId, type: 'APPROVE' });
                                  }}
                                >
                                  <FaCheck />
                                </button>
                                <button title="Reject" style={actionBtnReject} onClick={() => setConfirmation({ id: visibilityId, type: 'REJECT' })}><FaTimes /></button>
                              </>
                            )}
                            {v.status === "Approved" && visibilityId && (isSystemAdmin || isOrgAdmin) && (
                              <button title="Disable" style={actionBtnDisable} onClick={() => setConfirmation({ id: visibilityId, type: 'DISABLE' })}><FaBan size={12} /></button>
                            )}
                            {visibilityId && (isSystemAdmin || isOrgAdmin) && (
                              <button title="Delete" style={actionBtnDelete} onClick={() => setConfirmation({ id: visibilityId, type: 'DELETE' })}><FaTrash /></button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={paginationRow}>
              <div style={paginationInfoStyle}>Showing <b>{currentItems.length}</b> of <b>{filteredVisibilities.length}</b> items</div>
              <div style={paginationControlsWrapper}>
                <button style={pageArrow} disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}><FaChevronLeft /></button>
                {visiblePages.map(page => (
                  <button key={page} onClick={() => setCurrentPage(page)} style={{ ...pageNumberBtn, ...(currentPage === page ? activePage : {}) }}>{page}</button>
                ))}
                <button style={pageArrow} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}><FaChevronRight /></button>
              </div>
            </div>
          )}
        </div>
      </div>
        {/* DETAILS MODAL */}
        {showDetailsModal && (
          <div style={modalOverlayStyle} onClick={() => setShowDetailsModal(false)}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
              <div style={modalHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={modalIconWrapper}>
                    <FaEye color="#2563eb" size={18} />
                  </div>
                  <div>
                    <h3 style={modalTitleStyle}>Visibility Rule Details</h3>
                    <div style={modalSubtitleStyle}>Configuration and runtime evaluation conditions</div>
                  </div>
                </div>
                <button
                  style={modalCloseBtnStyle}
                  onClick={() => setShowDetailsModal(false)}
                  title="Close"
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div style={modalBodyStyle}>
                {fetchingDetails ? (
                  <div style={modalLoadingStateStyle}>
                    <FaSyncAlt className="spin" size={24} color="#6366f1" />
                    <div style={{ marginTop: 12, color: '#64748b', fontSize: 14 }}>Loading visibility details...</div>
                  </div>
                ) : selectedVisibilityDetails ? (
                  <div>
                    <div style={detailHeaderCardStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                        <div>
                          <h4 style={detailRuleNameStyle}>{selectedVisibilityDetails.name || 'Unnamed Rule'}</h4>
                          <div style={detailRuleDescStyle}>{selectedVisibilityDetails.description || 'No description provided'}</div>
                        </div>
                        <span style={getBadgeStyle(selectedVisibilityDetails.status)}>
                          <FaCircle size={6} /> {selectedVisibilityDetails.status || 'Draft'}
                        </span>
                      </div>

                      <div style={detailGridStyle}>
                        <div>
                          <span style={detailFieldLabelStyle}>Visibility ID</span>
                          <div style={detailFieldValueStyle}>{selectedVisibilityDetails.visibilityId || selectedVisibilityDetails.id || 'N/A'}</div>
                        </div>
                        <div>
                          <span style={detailFieldLabelStyle}>Flow ID</span>
                          <div style={detailFieldValueStyle}>{selectedVisibilityDetails.flowId || 'N/A'}</div>
                        </div>
                        <div>
                          <span style={detailFieldLabelStyle}>Site ID</span>
                          <div style={detailFieldValueStyle}>{selectedVisibilityDetails.siteId || 'N/A'}</div>
                        </div>
                        <div>
                          <span style={detailFieldLabelStyle}>Organization ID</span>
                          <div style={detailFieldValueStyle}>{selectedVisibilityDetails.organizationId || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 20 }}>
                      <div style={rulesSectionTitleStyle}>
                        <span>Rule Conditions</span>
                        <span style={rulesCountBadgeStyle}>
                          {Array.isArray(selectedVisibilityDetails.rules) ? selectedVisibilityDetails.rules.length : 0} {selectedVisibilityDetails.rules?.length === 1 ? 'Condition' : 'Conditions'}
                        </span>
                      </div>

                      {Array.isArray(selectedVisibilityDetails.rules) && selectedVisibilityDetails.rules.length > 0 ? (
                        <div style={rulesTableWrapperStyle}>
                          <table style={rulesTableStyle}>
                            <thead>
                              <tr style={rulesTableHeaderRowStyle}>
                                <th style={rulesThStyle}>Type</th>
                                <th style={rulesThStyle}>Operator</th>
                                <th style={rulesThStyle}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedVisibilityDetails.rules.map((rule, idx) => (
                                <tr key={idx} style={rulesTrStyle}>
                                  <td style={rulesTdStyle}>
                                    <span style={ruleTypeBadgeStyle}>{rule.type || 'N/A'}</span>
                                  </td>
                                  <td style={rulesTdStyle}>
                                    <span style={ruleOperatorStyle}>{rule.operator || 'N/A'}</span>
                                  </td>
                                  <td style={rulesTdStyle}>
                                    <code style={ruleValueStyle}>{String(rule.value ?? '')}</code>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={emptyRulesBoxStyle}>
                          No conditions configured for this visibility rule.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={modalErrorStateStyle}>
                    <FaExclamationTriangle size={24} color="#f59e0b" />
                    <div style={{ marginTop: 12, color: '#64748b' }}>Could not load visibility details.</div>
                  </div>
                )}
              </div>

              <div style={modalFooterStyle}>
                <button
                  style={modalCloseBtnSecondaryStyle}
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .spin { animation: fa-spin 2s infinite linear; }
        @keyframes fa-spin { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
        .kpi-card { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; overflow: hidden; }
        .kpi-card:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important; }
        .kpi-card:active { transform: translateY(-2px) scale(0.98); }
        .kpi-card::after { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(225deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%); pointer-events: none; }
      `}</style>
    </div>
  );
}

// ============ DROPDOWN STYLES ============
const dropdownContainerStyle = { position: 'relative' };
const dropdownTriggerStyle = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontWeight: 500, color: '#1e293b', transition: 'all 0.2s ease' };
const dropdownTriggerTextStyle = { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const dropdownMenuStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, marginTop: 4, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', zIndex: 1000, overflow: 'hidden', animation: 'fadeIn 0.2s ease-out' };
const searchBoxContainerStyle = { position: 'relative', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const searchIconStyle = { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12, pointerEvents: 'none' };
const dropdownSearchInputStyle = { width: '100%', padding: '8px 12px 8px 28px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#fff' };
const dropdownListStyle = { maxHeight: '240px', overflowY: 'auto', padding: '4px' };
const dropdownOptionStyle = { width: '100%', padding: '10px 12px', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 6, margin: '2px 0', transition: 'all 0.2s ease', fontSize: 13 };
const optionTextStyle = { flex: 1 };
const optionNameStyle = { fontWeight: 600, color: 'inherit' };
const optionIdStyle = { fontSize: '11px', opacity: 0.6, marginTop: 2 };
const emptyOptionStyle = { padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: 13 };
const searchableDropdownStyles = {
  container: dropdownContainerStyle,
  trigger: dropdownTriggerStyle,
  triggerText: dropdownTriggerTextStyle,
  menu: dropdownMenuStyle,
  searchContainer: searchBoxContainerStyle,
  searchIcon: searchIconStyle,
  searchInput: dropdownSearchInputStyle,
  list: dropdownListStyle,
  option: dropdownOptionStyle,
  optionText: optionTextStyle,
  optionName: optionNameStyle,
  optionId: optionIdStyle,
  emptyOption: emptyOptionStyle
};

// ============ MAIN STYLES ============
const kpiRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '20px', marginBottom: '32px', alignItems: 'stretch' };
const kpiCardBase = { padding: '24px', borderRadius: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: '160px', cursor: 'pointer', transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', border: '1px solid transparent' };
const kpiCardWhite = { ...kpiCardBase, background: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' };

const kpiCardActiveAll = { ...kpiCardBase, border: '2px solid #6366f1', background: '#f5f3ff', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.1)' };
const kpiCardActivePending = { ...kpiCardBase, border: '2px solid #f59e0b', background: '#fffbeb', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(245, 158, 11, 0.1)' };
const kpiCardActiveSubmitted = { ...kpiCardBase, border: '2px solid #a855f7', background: '#f3e8ff', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(168, 85, 247, 0.1)' };
const kpiCardActiveApproved = { ...kpiCardBase, border: '2px solid #10b981', background: '#f0fdf4', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.1)' };
const kpiCardActiveRejected = { ...kpiCardBase, border: '2px solid #ef4444', background: '#fef2f2', color: '#1e293b', boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.1)' };
const kpiCardActiveDisabled = {
  ...kpiCardBase,
  border: '2px solid #9ca3af',
  background: '#f9fafb',
  color: '#1e293b',
  boxShadow: '0 10px 15px -3px rgba(107, 114, 128, 0.1)'
};
const kpiHeader = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' };
const kpiIconWrapper = { width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' };
const kpiLabelStyle = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' };
const kpiValueStyle = { fontSize: '32px', fontWeight: 800, marginBottom: '4px', color: '#1e293b' };
const kpiMetaStyle = { fontSize: '11px', opacity: 0.7, fontWeight: 500 };

const flowWarningBannerStyle = { display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '16px 20px', borderRadius: '14px', background: '#fef3c7', color: '#92400e', border: '1.5px solid #fcd34d', marginBottom: '24px', fontSize: '14px', boxShadow: '0 4px 6px -1px rgba(251, 191, 36, 0.1)' };
const flowStatusBadge = { display: 'inline-block', padding: '2px 8px', borderRadius: '12px', background: '#fed7aa', color: '#7c2d12', fontWeight: 700, fontSize: '12px', marginLeft: 4 };

const inlineConfirmWrapper = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '4px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.2s ease-out' };
const confirmText = { fontSize: '12px', fontWeight: '700', color: '#475569' };
const btnYes = { background: '#6366f1', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };
const btnNo = { background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };

const wrapperStyle = { width: '100%', minHeight: '100vh', background: '#f8fafc', paddingBottom: 60, fontFamily: 'Inter, sans-serif' };
const containerStyle = { maxWidth: 1200, margin: '0 auto', padding: '24px' };
const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = { width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' };
const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' };
const filtersCardStyle = { background: '#fff', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #e2e8f0' };
const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, borderBottom: '1px solid #f1f5f9', paddingBottom: 12 };
const cardTitleStyle = { fontSize: 15, fontWeight: 700 };
const filtersGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'end' };
const inputGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b' };
const primaryButtonStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 };
const tableCardStyle = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' };
const tableActionsHeader = { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#fcfdfe' };
const searchWrapper = { position: 'relative', maxWidth: 300 };
const searchIcon = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };
const searchInput = { width: '100%', padding: '8px 12px 8px 36px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#fff', color: '#3a4557' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = { padding: '14px 20px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left' };
const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '16px 20px', fontSize: 14 };
const ruleTitle = { fontWeight: 700, color: '#1e293b' };
const ruleIdText = { fontSize: '10px', color: '#94a3b8', marginTop: 2 };
const ruleDesc = { fontSize: 12, color: '#64748b', marginTop: 2 };
const tdActionStyle = { padding: '16px 20px', textAlign: 'right' };
const actionButtonGroupStyle = { display: 'inline-flex', gap: 8 };
const actionBtnBase = { width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const actionBtnApprove = { ...actionBtnBase, background: '#dcfce7', color: '#166534' };
const actionBtnReject = { ...actionBtnBase, background: '#fee2e2', color: '#991b1b' };
const actionBtnDisable = { ...actionBtnBase, background: '#f3e8ff', color: '#6b7280' };
const actionBtnDelete = { ...actionBtnBase, background: '#f1f5f9', color: '#64748b' };
const emptyStateStyle = { padding: '60px 20px', textAlign: 'center', color: '#94a3b8' };
const paginationRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 16 };
const paginationInfoStyle = { fontSize: 14, color: '#64748b', fontWeight: 500 };
const paginationControlsWrapper = { display: 'flex', alignItems: 'center', gap: 8 };
const pageNumberBtn = { minWidth: 36, height: 36, padding: '0 4px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: '700', cursor: 'pointer', color: '#64748b' };
const activePage = { background: '#6366f1', color: '#fff', borderColor: '#6366f1', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)' };
const pageArrow = { ...pageNumberBtn, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const actionBtnView = { ...actionBtnBase, background: '#eff6ff', color: '#2563eb' };

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: '20px'
};

const modalContentStyle = {
  background: '#fff',
  borderRadius: 16,
  width: '100%',
  maxWidth: 680,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  border: '1px solid #e2e8f0',
  animation: 'fadeIn 0.2s ease-out',
  overflow: 'hidden'
};

const modalHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderBottom: '1px solid #f1f5f9',
  background: '#fcfdfe'
};

const modalIconWrapper = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const modalTitleStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
  margin: 0
};

const modalSubtitleStyle = {
  fontSize: 12,
  color: '#64748b',
  marginTop: 2
};

const modalCloseBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: '#94a3b8',
  cursor: 'pointer',
  padding: 6,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  transition: 'color 0.2s'
};

const modalBodyStyle = {
  padding: '24px',
  overflowY: 'auto',
  flex: 1
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '16px 24px',
  borderTop: '1px solid #f1f5f9',
  background: '#f8fafc'
};

const modalCloseBtnSecondaryStyle = {
  background: '#fff',
  color: '#475569',
  border: '1.5px solid #e2e8f0',
  padding: '8px 18px',
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer'
};

const modalLoadingStateStyle = {
  padding: '40px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const modalErrorStateStyle = {
  padding: '40px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const detailHeaderCardStyle = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '16px 20px'
};

const detailRuleNameStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: '#1e293b',
  margin: '0 0 4px 0'
};

const detailRuleDescStyle = {
  fontSize: 13,
  color: '#64748b',
  lineHeight: 1.4
};

const detailGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px 16px',
  marginTop: 16,
  paddingTop: 14,
  borderTop: '1px solid #e2e8f0'
};

const detailFieldLabelStyle = {
  display: 'block',
  fontSize: 10,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 2
};

const detailFieldValueStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#334155',
  fontFamily: 'monospace',
  wordBreak: 'break-all'
};

const rulesSectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
  fontSize: 14,
  fontWeight: 700,
  color: '#1e293b'
};

const rulesCountBadgeStyle = {
  background: '#e0e7ff',
  color: '#4338ca',
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 12
};

const rulesTableWrapperStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  overflow: 'hidden'
};

const rulesTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  textAlign: 'left'
};

const rulesTableHeaderRowStyle = {
  background: '#f1f5f9'
};

const rulesThStyle = {
  padding: '10px 14px',
  fontSize: 11,
  fontWeight: 700,
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const rulesTrStyle = {
  borderBottom: '1px solid #f1f5f9'
};

const rulesTdStyle = {
  padding: '12px 14px',
  fontSize: 13,
  color: '#1e293b'
};

const ruleTypeBadgeStyle = {
  background: '#f3e8ff',
  color: '#7e22ce',
  padding: '3px 8px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 700
};

const ruleOperatorStyle = {
  fontWeight: 600,
  color: '#0369a1',
  background: '#e0f2fe',
  padding: '3px 8px',
  borderRadius: 6,
  fontSize: 11
};

const ruleValueStyle = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  padding: '2px 6px',
  borderRadius: 4,
  fontSize: 12,
  color: '#0f172a'
};

const emptyRulesBoxStyle = {
  padding: '24px',
  textAlign: 'center',
  color: '#94a3b8',
  fontSize: 13,
  background: '#f8fafc',
  borderRadius: 10,
  border: '1px dashed #cbd5e1'
};